import { useEffect, useState } from "react";
import Layout from "../../../components/layouts/Layout";
import Section from "../../../components/base/Section";
import Indicator from "../../../components/base/Indicator";
import Icon from "../../../components/base/Icon";
import { clearToolbar, isRoot, setPage, setToolbarActions, useHub } from "../../../core/Global";
import { toastError } from "../../../components/base/Toast";
import { CatalogOverview, CatalogRow } from "../models/PlanCatalog";
import PlanCatalogCard from "../components/PlanCatalogCard";
import PlanEditDrawer from "../components/PlanEditDrawer";
import PricesDrawer from "../components/PricesDrawer";
import ApplyDrawer from "../components/ApplyDrawer";
import HistoryDrawer from "../components/HistoryDrawer";
import "./PlanCatalogView.css";


// The plans Rinvox sells and every figure on them -- price, allowances, overage, what is
// included -- plus the general prices that belong to no one plan. Rinvox's own page, root only.
//
// An edit here is for the next customer to buy. Customers already subscribed keep what they
// bought until root sends the edit on to them, which takes effect at each one's own renewal.
export default function PlanCatalogView() {

    const hub = useHub()

    const [data, dataSet] = useState<CatalogOverview>()
    const [loading, loadingSet] = useState<boolean>(true)

    const [editing, editingSet] = useState<CatalogRow>()
    const [applying, applyingSet] = useState<CatalogRow>()
    const [pricesOpen, pricesOpenSet] = useState<boolean>(false)
    const [historyOpen, historyOpenSet] = useState<boolean>(false)

    const load = (silent: boolean = false) => {
        if (!silent) loadingSet(true)
        hub.Get("/system/plancatalog/", res => {
            loadingSet(false)
            if (res.success) dataSet(res.data)
            else toastError(res.message ?? "Couldn't load the plans.")
        }, () => loadingSet(false))
    }

    useEffect(() => {
        setPage("Plan Catalog")
        if (isRoot()) load()
        return () => { clearToolbar() }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // The page's buttons live in the header, beside its title. Set again whenever what they
    // depend on changes -- the loaded data and the refresh spinner.
    useEffect(() => {
        if (!isRoot()) return
        setToolbarActions(() => (
            <>
                <button type="button" className="btn btn-sm btn-light" disabled={!data}
                    onClick={() => pricesOpenSet(true)}>
                    <Icon icon="tags" type="solid" size={12} className="me-2" />
                    General prices
                </button>
                <button type="button" className="btn btn-sm btn-light" disabled={!data}
                    onClick={() => historyOpenSet(true)}>
                    <Icon icon="clock-rotate-left" type="solid" size={12} className="me-2" />
                    History
                </button>
                <button type="button" className="btn btn-sm btn-light-primary" disabled={loading}
                    onClick={() => load()}>
                    <Icon icon="arrows-rotate" type="solid" size={12} className={"me-2" + (loading ? " fa-spin" : "")} />
                    Refresh
                </button>
            </>
        ))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, data])

    if (!isRoot()) {
        return (
            <Layout wide>
                <Section fluid className="py-20 text-center text-gray-600">
                    This page is for the root account only.
                </Section>
            </Layout>
        )
    }

    const outdated = data?.plans.reduce((n, r) => n + r.outdated, 0) ?? 0

    return (
        <Layout wide>
            <Section fluid className="py-6 catalog-page">

                <div className="catalog-rule">
                    <Icon icon="circle-info" type="solid" size={14} />
                    <div>
                        Saving a plan changes it for <b>new purchases only</b>. Customers already on it keep what they
                        bought. To move them over too, use <b>Send to subscribers</b> on the plan: each one is told
                        today and changes at their own renewal — a price rise never sooner
                        than {data?.priceRiseNoticeDays ?? 14} days after the notice.
                        {
                            outdated > 0 &&
                            <span className="catalog-rule-count">
                                {outdated} subscription{outdated === 1 ? " is" : "s are"} on older terms.
                            </span>
                        }
                    </div>
                </div>

                {
                    loading && !data ?
                        <div className="py-20"><Indicator /></div>
                        :
                        <div className="catalog-grid">
                            {
                                data?.plans.map(r =>
                                    <PlanCatalogCard key={r.plan.code} row={r}
                                        onEdit={() => editingSet(r)}
                                        onApply={() => applyingSet(r)} />
                                )
                            }
                        </div>
                }

            </Section>

            {
                editing && data &&
                <PlanEditDrawer
                    row={editing}
                    overview={data}
                    onClose={() => editingSet(undefined)}
                    onSaved={() => load(true)} />
            }

            {
                applying && data &&
                <ApplyDrawer
                    row={applying}
                    noticeDays={data.priceRiseNoticeDays}
                    onClose={() => applyingSet(undefined)}
                    onApplied={() => load(true)} />
            }

            {
                pricesOpen && data &&
                <PricesDrawer
                    prices={data.prices}
                    onClose={() => pricesOpenSet(false)}
                    onSaved={() => load(true)} />
            }

            {
                historyOpen && data &&
                <HistoryDrawer
                    plans={data.plans.map(r => r.plan)}
                    onClose={() => historyOpenSet(false)} />
            }
        </Layout>
    )
}
